import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import SectionCard from "@/components/customers/SectionCard";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useCustomerCompanyInfo, useRefreshCompanyInfo } from "@/hooks/use-customer-company-info";
import { chairFromRoles, leaderFromRoles, revenueGrowthPct } from "@/lib/brreg-calc";
import { hasMinRole } from "@/lib/constants";
import { formatDate, formatNOK, formatPct, formatRelativeTime } from "@/lib/format";
import { TONE_TEXT } from "@/lib/widget-status";
import type { DBCustomerCompanyInfo } from "@/lib/db-types";

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border/50 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{children || "—"}</dd>
    </div>
  );
}

function GrowthCell({ cur, prev }: { cur: number | null; prev: number | null }) {
  const pct = revenueGrowthPct(cur, prev);
  if (pct == null) return null;
  return (
    <span className={`ml-2 text-xs ${pct >= 0 ? TONE_TEXT.reached : TONE_TEXT.miss}`}>
      {pct >= 0 ? "+" : ""}{formatPct(pct, 0)}
    </span>
  );
}

function AccountsTable({ info }: { info: DBCustomerCompanyInfo }) {
  if (info.fiscal_year == null) {
    return <p className="text-sm text-muted-foreground">Ingen levert regnskap i Regnskapsregisteret ennå.</p>;
  }
  const foreignCurrency = info.revenue_nok == null && info.operating_result_nok == null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 px-2 font-medium text-muted-foreground">År</th>
            <th className="text-right py-2 px-2 font-medium text-muted-foreground">Omsetning</th>
            <th className="text-right py-2 px-2 font-medium text-muted-foreground">Driftsresultat</th>
            <th className="text-right py-2 px-2 font-medium text-muted-foreground">Årsresultat</th>
            <th className="text-right py-2 px-2 font-medium text-muted-foreground">Egenkapital</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b">
            <td className="py-2 px-2">{info.fiscal_year}</td>
            <td className="py-2 px-2 text-right">
              {formatNOK(info.revenue_nok)}
              <GrowthCell cur={info.revenue_nok} prev={info.previous_revenue_nok} />
            </td>
            <td className="py-2 px-2 text-right">{formatNOK(info.operating_result_nok)}</td>
            <td className="py-2 px-2 text-right">{formatNOK(info.net_result_nok)}</td>
            <td className="py-2 px-2 text-right">{formatNOK(info.equity_nok)}</td>
          </tr>
          {info.previous_fiscal_year != null && (
            <tr className="text-muted-foreground">
              <td className="py-2 px-2">{info.previous_fiscal_year}</td>
              <td className="py-2 px-2 text-right">{formatNOK(info.previous_revenue_nok)}</td>
              <td className="py-2 px-2 text-right">—</td>
              <td className="py-2 px-2 text-right">—</td>
              <td className="py-2 px-2 text-right">—</td>
            </tr>
          )}
        </tbody>
      </table>
      {foreignCurrency && (
        <p className="mt-2 text-xs text-muted-foreground">
          Regnskapet er levert i en annen valuta enn NOK og vises ikke her.
        </p>
      )}
    </div>
  );
}

interface Props {
  customerId: string;
  organizationNumber: string | null;
  isCompany: boolean;
  /** Sidekolonnen på kundekortet («Bedriften»): nøkkeltall i én kolonne, ingen regnskapstabell. */
  compact?: boolean;
}

function CompactFacts({ info }: { info: DBCustomerCompanyInfo }) {
  const leader = leaderFromRoles(info.roles?.filter((r) => r.role_code === "DAGL"));
  const lifecycle = [
    info.founded_on ? `Stiftet ${new Date(info.founded_on).getUTCFullYear()}` : null,
    info.organization_form,
    info.is_bankrupt ? "konkurs" : info.is_winding_up ? "under avvikling" : "aktivt",
  ].filter(Boolean);
  return (
    <dl>
      {info.fiscal_year != null && (
        <>
          <InfoRow label={`Omsetning ${info.fiscal_year}`}>
            {info.revenue_nok != null && (
              <>
                {formatNOK(info.revenue_nok)}
                <GrowthCell cur={info.revenue_nok} prev={info.previous_revenue_nok} />
              </>
            )}
          </InfoRow>
          <InfoRow label="Resultat">{info.net_result_nok != null ? formatNOK(info.net_result_nok) : null}</InfoRow>
        </>
      )}
      <InfoRow label="Ansatte">{info.employees != null ? String(info.employees) : null}</InfoRow>
      <InfoRow label="Bransje">{info.industry_description}</InfoRow>
      <InfoRow label="Kommune">{info.municipality}</InfoRow>
      <InfoRow label="Daglig leder">{leader?.person_name}</InfoRow>
      {lifecycle.length > 0 && <p className="pt-2 text-xs text-muted-foreground">{lifecycle.join(" · ")}</p>}
    </dl>
  );
}

function FullFacts({ info }: { info: DBCustomerCompanyInfo }) {
  const leader = leaderFromRoles(info.roles?.filter((r) => r.role_code === "DAGL"));
  const chair = chairFromRoles(info.roles);
  return (
    <>
      <dl className="grid gap-x-8 sm:grid-cols-2">
        <InfoRow label="Organisasjonsform">{info.organization_form}</InfoRow>
        <InfoRow label="Bransje">
          {[info.industry_code, info.industry_description].filter(Boolean).join(" · ")}
        </InfoRow>
        <InfoRow label="Ansatte">{info.employees != null ? String(info.employees) : null}</InfoRow>
        <InfoRow label="Stiftet">{info.founded_on ? formatDate(info.founded_on) : null}</InfoRow>
        <InfoRow label="Kommune">{info.municipality}</InfoRow>
        <InfoRow label="Daglig leder">{leader?.person_name}</InfoRow>
        <InfoRow label="Styreleder">{chair?.person_name}</InfoRow>
        <InfoRow label="Nettside">
          {info.website && (
            <a href={info.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              {info.website.replace(/^https?:\/\//, "")}
            </a>
          )}
        </InfoRow>
      </dl>
      {info.last_error && <p className={`text-xs ${TONE_TEXT.miss}`}>Siste henting feilet: {info.last_error}</p>}
      <AccountsTable info={info} />
    </>
  );
}

/** Bedriftsinfo fra Brønnøysund (CRM-3.1). Rendres av kundekortet bak feature_crm_signaler. */
export default function CompanyInfoSection({ customerId, organizationNumber, isCompany, compact = false }: Props) {
  // Samme regel som edge function: ni sifre, mellomrom ignoreres.
  const canFetch = isCompany && (organizationNumber ?? "").replace(/\D/g, "").length === 9;
  const { data: info, isLoading } = useCustomerCompanyInfo(customerId, { enabled: canFetch });
  const refresh = useRefreshCompanyInfo();
  const { activeUser } = useImpersonation();
  const isAdmin = hasMinRole(activeUser?.role ?? "external", "admin");

  const emptyText = !canFetch
    ? "Hentes for selskaper med organisasjonsnummer (ni sifre). Legg inn organisasjonsnummer under Rediger."
    : info?.last_error
      ? info.last_error
      : `Ikke hentet fra Brønnøysund ennå. Det skjer automatisk i natt${isAdmin ? ", eller trykk Oppdater nå." : "."}`;
  const hasInfo = !!info?.legal_name;

  return (
    <SectionCard
      compact={compact}
      title={compact ? "Bedriften" : "Bedriftsinfo"}
      meta={info && hasInfo ? `${compact ? "Brønnøysund · " : "Sist oppdatert: "}${formatRelativeTime(info.fetched_at)}` : undefined}
      empty={emptyText}
      actions={
        <>
          {info?.is_bankrupt && <StatusBadge tone="miss" label="Konkurs" />}
          {info?.is_winding_up && <StatusBadge tone="miss" label="Under avvikling" />}
          {isAdmin && canFetch && (
            <Button
              size="sm"
              variant="outline"
              aria-label="Oppdater bedriftsinfo fra Brønnøysund"
              disabled={refresh.isPending}
              onClick={() => refresh.mutate(customerId)}
            >
              {refresh.isPending ? "Henter …" : "Oppdater nå"}
            </Button>
          )}
        </>
      }
    >
      {!canFetch ? null : isLoading ? (
        <Skeleton className="m-4 h-24" />
      ) : !info || !hasInfo ? null : (
        <div className={compact ? "px-4 py-2" : "space-y-4 px-5 py-4"}>
          {compact ? <CompactFacts info={info} /> : <FullFacts info={info} />}
        </div>
      )}
    </SectionCard>
  );
}
